import { createSelector } from "@reduxjs/toolkit";
import { FromType } from "client-server-shared/constants";
import { InfoBaseItem } from "client-server-shared/types";
import { custom } from "src/constants";
import { RootState } from "reducers/types";
import {
  ConnectedDomain,
  ConnectedWebsite,
} from "client-server-shared/types/connected-website";

export const selectContentfulAccessToken = (state: RootState) =>
  state.settings.contentful.accessToken;

export const selectContentfulSpaceId = (state: RootState) =>
  state.settings.contentful.spaceId;

export const selectContentfulEnvironmentId = (state: RootState) =>
  state.settings.contentful.environment;

export const selectContentfulContentType = (state: RootState) =>
  state.settings.contentful.contentType;

export const selectIsHydrated = (state: RootState) => state.settings.hydrated;

const selectSettings = (state: RootState) => state.settings;

export const selectStyles = createSelector(selectSettings, (settings) => {
  return settings.styles;
});

export const selectStylesToOptionsFormat = createSelector(
  selectStyles,
  (styles) => {
    return styles.map((style) => ({
      label: style.name,
      value: style.description,
      isCustom: true,
    }));
  }
);

const selectSourceFiles = createSelector(
  selectSettings,
  (settings) => settings.files
);

export const selectSourceFilesForChat = createSelector(
  selectSourceFiles,
  (files) => files.filter((file) => file.type === FromType.fromChatFileUpload)
);

export const selectInfoBaseItems = createSelector(selectSourceFiles, (files) =>
  files.filter((file) => file.type === FromType.info)
);

const emptyInfoBaseItems: InfoBaseItem[] = [];

export const selectInfoBaseItemsByIds = createSelector(
  [selectInfoBaseItems, (state: RootState, ids: string[]) => ids],
  (items, ids) => {
    if (ids.length === 0) return emptyInfoBaseItems;
    return items.filter((item) => ids.includes(item.id));
  }
);

export const selectStylesByStyleValue = createSelector(
  [selectStyles, (state, styleValue) => styleValue],
  (styles, styleValue) => {
    return styles.find((style) => style.description === styleValue);
  }
);

export const selectImages = createSelector(
  selectSettings,
  (settings) => settings.images
);

export const selectPrompts = createSelector(selectSettings, (settings) =>
  settings.prompts.map((prompt) => ({
    ...prompt,
    categories: [custom],
  }))
);

const defaultDomains: ConnectedDomain[] = [];
export const selectConnectedWebsites = createSelector(
  selectSettings,
  (settings) => {
    const domains: ConnectedDomain[] = [];
    if (!settings.websites) {
      return defaultDomains;
    }
    for (const domain in settings.websites) {
      const foundDomain = settings.websites[domain];

      if (foundDomain.websites.length > 0) {
        domains.push(foundDomain);
      }
    }

    return domains;
  }
);

export const selectFirstConnectedWebsiteUrl = createSelector(
  selectSettings,
  (settings) => {
    if (settings.websites) {
      const keys = Object.keys(settings.websites);
      if (keys.length > 0) {
        const firstKey = keys[0];

        return firstKey;
      }
    }
    return null;
  }
);

const emptyWebsiteIds: string[] = [];

export const selectHasConnectedWebsites = createSelector(
  selectSettings,
  (settings) => {
    return settings.websites && Object.keys(settings.websites).length > 0;
  }
);

export const selectValidWebsiteIds = createSelector(
  [selectSettings, (state, ids: string[]) => ids],
  (settings, ids) => {
    if (!settings.websites || ids.length === 0) {
      return emptyWebsiteIds;
    }
    const allWebsites = Object.values(settings.websites)
      .map((website) => website.websites.map((website) => website.id))
      .flat();
    return ids.filter((id) => allWebsites.includes(id));
  }
);
